import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { XIcon } from 'lucide-react';
import { ClientOverviewResponse } from '@/DTO/Clients/ClientOverviewResponse';
import UIStrings from '@/Utils/UIStrings';
import { clientFormFieldNames, ClientFormValues, clientSchema } from '../Client';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
    Button,
    Label,
} from '@/Components/shadcn';
import ClientDescription from './ClientDescription';
import ClientImageCrop, { ClientImageCropProps } from './ClientImageCrop';
import { cn } from '@/lib/utils';
import { RHFInput } from '@/Components/shadcn/rhf-input';

interface ClientFormDialogShadcnProps extends Partial<Omit<ClientImageCropProps, 'src'>> {
    open: boolean;
    client?: ClientOverviewResponse;
    onClose: () => void;
    onSubmit: (form: ClientFormValues) => Promise<void>;
}

const ClientFormDialog = ({
    client,
    open,
    onClose,
    onSubmit,
    handleUploadImage,
    handleResetImage,
}: ClientFormDialogShadcnProps) => {
    const form = useForm<ClientFormValues>({
        resolver: yupResolver(clientSchema),
        mode: 'all',
    });
    const {
        reset,
        register,
        formState: { isDirty, disabled, isSubmitting, errors },
        handleSubmit,
    } = form;
    const isEditMode = client !== undefined;

    useEffect(() => {
        if (open) {
            reset({
                description: client?.description ?? '',
                linkedInURL: client?.linkedInURL ?? '',
                name: client?.name ?? '',
                websiteURL: client?.websiteURL ?? '',
            });
        }
    }, [open, client, reset]);

    return (
        <FormProvider {...form}>
            <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
                <DialogContent
                    data-cy="ClientEditDialog"
                    role="presentation"
                    showCloseButton={false}
                    className={cn('max-h-11/12 overflow-y-auto', isEditMode ? 'max-w-4xl' : 'max-w-md')}
                >
                    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                        <DialogHeader className="flex flex-row items-center justify-between gap-4 space-y-0 text-left">
                            <DialogTitle className="flex-1">
                                {isEditMode ? UIStrings.EditCompany : UIStrings.CreateCompany}
                            </DialogTitle>
                            <DialogDescription className="sr-only">
                                {isEditMode ? UIStrings.EditCompany : UIStrings.CreateCompany}
                            </DialogDescription>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                data-cy="CloseDialogButton"
                                aria-label="close"
                                className="shrink-0"
                                onClick={onClose}
                            >
                                <XIcon />
                                <span className="sr-only">Close</span>
                            </Button>
                        </DialogHeader>
                        <div className="flex flex-col gap-4 p-1">
                            <div className="grid gap-2">
                                <Label htmlFor={clientFormFieldNames.name}>{UIStrings.CompanyName}*</Label>
                                <RHFInput
                                    id={clientFormFieldNames.name}
                                    {...register('name')}
                                    className={errors.name ? 'border-destructive' : ''}
                                />
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor={clientFormFieldNames.websiteURL}>{UIStrings.WebsiteUrl}*</Label>
                                <RHFInput
                                    id={clientFormFieldNames.websiteURL}
                                    {...register('websiteURL')}
                                    className={errors.websiteURL ? 'border-destructive' : ''}
                                />
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor={clientFormFieldNames.linkedInURL}>{UIStrings.LinkedInUrl}</Label>
                                <RHFInput
                                    id={clientFormFieldNames.linkedInURL}
                                    {...register('linkedInURL')}
                                    className={errors.linkedInURL ? 'border-destructive' : ''}
                                />
                            </div>
                            {isEditMode && handleUploadImage && handleResetImage && client && (
                                <ClientImageCrop
                                    src={client.iconURL ?? ''}
                                    handleUploadImage={handleUploadImage}
                                    handleResetImage={handleResetImage}
                                />
                            )}
                            {isEditMode && client && <ClientDescription initialHtml={client.description} />}
                        </div>
                        <DialogFooter>
                            <Button
                                type="submit"
                                data-cy="ClientEditDialogSubmit"
                                disabled={!isDirty || disabled || isSubmitting}
                            >
                                {isSubmitting && (
                                    <span className="flex items-center gap-2">
                                        <span className="border-primary-foreground size-4 animate-spin rounded-full border-2 border-t-transparent" />
                                    </span>
                                )}
                                {isEditMode ? UIStrings.Update : UIStrings.CreateCompany}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </FormProvider>
    );
};

export default ClientFormDialog;
